/**
 * P3-S4 FS-11-08 교류 룸 초대·수락(교사 · SR-ROOM-05 · V13 room_invite — 계약 §3 교사 API · 세션 역할 teacher · 코스 경계):
 *   GET  /api/courses/{c}/teacher/exchange/courses → [{ courseId, label, teacherCount }]
 *        (같은 기관 · 끝나지 않은 · 최근 Field 사용 코스 · 자기 코스 제외 · label 만 — 교사 이름 없음)
 *   GET  …/teacher/exchange/invites → { sent: Invite[], received: Invite[] }
 *        Invite = { inviteId, fromCourse:{courseId,label}, toCourse:{courseId,label},
 *                   state: pending|accepted|declined|expired|cancelled, scheduledAt, expiresAt, createdAt, respondedAt, roomId, roomState }
 *   POST …/teacher/exchange/invites { toCourseId, scheduledAt?, expiresInDays(1..30 · 기본 7) } → 201 Invite
 *        · 409 같은 쌍 대기 중 · 403 FE-302 다른 기관
 *   POST …/invites/{id}/accept | decline | cancel → 200 Invite
 *   POST …/invites/{id}/open(호스트 = 보낸 코스 교사 · accepted 만) → 룸 열기 응답과 같은 모양({ room }) · 이미 열려 있으면 그것
 * 룸 코드 입력 없음(SR-AUTH-03) — 초대 id 로만. 점수·반 비교 필드 없음(DR-01).
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { Requester } from "../../../types/api";
import type { InviteState, ExchangeCourse, CourseRef, Invite, Invites, InviteBody } from "../types";

export const INVITE_STATES: readonly InviteState[] = [
  "pending",
  "accepted",
  "declined",
  "expired",
  "cancelled",
];

/** 만료 고르기(일) — 서버 범위 1..30 · 기본 7 */
export const EXPIRY_DAYS: readonly number[] = [1, 3, 7, 14, 30];
export const DEFAULT_EXPIRY_DAYS = 7;

/** 409 — 같은 두 반 사이에 대기 중인 초대가 이미 있다(또는 열린 교류 룸이 있다) */
export class InviteConflict extends Error {
  constructor() {
    super("invite conflict");
    this.name = "InviteConflict";
  }
}

export interface ExchangeApi {
  courses(): Promise<ExchangeCourse[]>;
  invites(): Promise<Invites>;
  send(body: InviteBody): Promise<Invite | null>;
  respond(inviteId: string, op: "accept" | "decline" | "cancel"): Promise<Invite | null>;
  /** 교류 룸 열기(호스트) — 열린 룸 id */
  open(inviteId: string): Promise<string>;
}

const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
const idOf = (v: unknown): string | null =>
  str(v) ?? (typeof v === "number" && Number.isFinite(v) ? String(v) : null);

function readRef(v: unknown): CourseRef | null {
  const o = obj(v);
  const courseId = idOf(o?.courseId);
  if (!o || !courseId) return null;
  return { courseId, label: str(o.label) ?? courseId };
}

/** 초대 한 줄을 너그럽게 읽는다 — 모양이 어긋나면 null(목록에서 뺀다) */
export function readInvite(v: unknown): Invite | null {
  const o = obj(v);
  const inviteId = idOf(o?.inviteId);
  const from = readRef(o?.fromCourse);
  const to = readRef(o?.toCourse);
  if (!o || !inviteId || !from || !to) return null;
  const st = str(o.state);
  return {
    inviteId,
    fromCourse: from,
    toCourse: to,
    state:
      st && (INVITE_STATES as readonly string[]).includes(st) ? (st as InviteState) : "pending",
    scheduledAt: str(o.scheduledAt),
    expiresAt: str(o.expiresAt),
    createdAt: str(o.createdAt),
    respondedAt: str(o.respondedAt),
    roomId: idOf(o.roomId),
    roomState: str(o.roomState),
  };
}

const list = (v: unknown): Invite[] =>
  (Array.isArray(v) ? v : []).map(readInvite).filter((x): x is Invite => !!x);

export function readInvites(v: unknown): Invites {
  const o = obj(v) ?? {};
  return { sent: list(o.sent), received: list(o.received) };
}

export function readCourses(v: unknown): ExchangeCourse[] {
  const xs = Array.isArray(v) ? v : (obj(v)?.courses ?? obj(v)?.items);
  return (Array.isArray(xs) ? xs : []).flatMap((x) => {
    const r = readRef(x);
    if (!r) return [];
    const n = obj(x)?.teacherCount;
    return [{ ...r, teacherCount: typeof n === "number" && n > 0 ? Math.floor(n) : 1 }];
  });
}

export function createExchangeApi(request: Requester, courseId: string): ExchangeApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/exchange`;
  const post = (body?: unknown): RequestInit =>
    body === undefined
      ? { method: "POST" }
      : {
          method: "POST",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        };
  const conflict = (e: unknown): never => {
    if (e instanceof ApiError && e.status === 409) throw new InviteConflict();
    throw e;
  };
  return {
    courses: async () => readCourses(await request<unknown>(`${c}/courses`)),
    invites: async () => readInvites(await request<unknown>(`${c}/invites`)),
    async send(body) {
      try {
        return readInvite(await request<unknown>(`${c}/invites`, post(body)));
      } catch (e) {
        return conflict(e);
      }
    },
    async respond(inviteId, op) {
      try {
        return readInvite(
          await request<unknown>(`${c}/invites/${encodeURIComponent(inviteId)}/${op}`, post()),
        );
      } catch (e) {
        return conflict(e);
      }
    },
    async open(inviteId) {
      try {
        const r = obj(
          await request<unknown>(`${c}/invites/${encodeURIComponent(inviteId)}/open`, post()),
        );
        const room = obj(r?.room) ?? r;
        const id = idOf(room?.roomId);
        if (!id) throw new Error("room");
        return id;
      } catch (e) {
        return conflict(e);
      }
    },
  };
}

export function useExchangeApi(): ExchangeApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createExchangeApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
